import type { CollaborationPostStatus, User } from "features/user";

const POST_STATUS_VERSION_ENTITLEMENT = "post-status-version";
const WORK_IN_PROGRESS_LABEL = "Work in progress";
const APPROVED_TO_PUBLISH_LABEL = "Approved to publish";

type RuntimePostStatus = CollaborationPostStatus & {
  index?: number;
  label?: string;
  isDefault?: boolean;
  isPublishingAllowed?: boolean;
  key?: string;
};

interface BasicProjectionMetadata {
  workInProgressKey: string;
  defaultKey: string;
  readOnly: boolean;
  pendingMigration: boolean;
  sourceToTarget: ReadonlyMap<string, string>;
  targetToSources: ReadonlyMap<string, readonly string[]>;
}

const effectiveBasicStatusesCache = new WeakMap<
  User,
  {
    source: User["postStatuses"];
    result: CollaborationPostStatus[];
  }
>();
const basicProjectionMetadata = new WeakMap<object, BasicProjectionMetadata>();

export const hasAuthoritativeBasicPostStatuses = (user: User): boolean => {
  const entitlements = user.preferences?.entitlements ?? {};

  return (
    Object.prototype.hasOwnProperty.call(
      entitlements,
      POST_STATUS_VERSION_ENTITLEMENT
    ) && entitlements[POST_STATUS_VERSION_ENTITLEMENT] === "basic"
  );
};

export const isPostStatusCatalogHydrated = (
  user: User | null | undefined
): boolean => {
  if (!user || !Array.isArray(user.postStatuses)) return false;

  return (
    !hasAuthoritativeBasicPostStatuses(user) ||
    Boolean(user.userId) ||
    user.postStatuses.length > 0
  );
};

const statusKey = (status: RuntimePostStatus): string | undefined =>
  typeof status.key === "string" && status.key.length > 0
    ? status.key
    : undefined;

const orderedStatuses = (
  statuses: readonly RuntimePostStatus[]
): RuntimePostStatus[] =>
  statuses
    .map((status, originalIndex) => ({ status, originalIndex }))
    // eslint-disable-next-line unicorn/no-array-sort -- Preserve the client's supported browsers without requiring Array#toSorted.
    .sort((left, right) => {
      const leftIndex =
        typeof left.status.index === "number"
          ? left.status.index
          : Number.MAX_SAFE_INTEGER;
      const rightIndex =
        typeof right.status.index === "number"
          ? right.status.index
          : Number.MAX_SAFE_INTEGER;
      return leftIndex - rightIndex || left.originalIndex - right.originalIndex;
    })
    .map(({ status }) => status);

const safeSemanticKeys = (
  statuses: readonly RuntimePostStatus[],
  publishingAllowed: boolean
): Set<string> => {
  const semantics = new Map<string, Set<boolean | undefined>>();
  for (const status of statuses) {
    const key = statusKey(status);
    if (!key) continue;

    const values = semantics.get(key) ?? new Set<boolean | undefined>();
    values.add(
      status.isPublishingAllowed === true
        ? true
        : status.isPublishingAllowed === false
          ? false
          : undefined
    );
    semantics.set(key, values);
  }

  return new Set(
    [...semantics.entries()]
      .filter(
        ([, values]) => values.size === 1 && values.has(publishingAllowed)
      )
      .map(([key]) => key)
  );
};

const chooseTargetStatus = (
  statuses: readonly RuntimePostStatus[],
  safeKeys: ReadonlySet<string>,
  preferredLabel: string,
  excludedKey?: string
): RuntimePostStatus | undefined => {
  const candidates = orderedStatuses(statuses).filter((status) => {
    const key = statusKey(status);
    return key && safeKeys.has(key) && key !== excludedKey;
  });
  return (
    candidates.find((status) => status.label === preferredLabel) ??
    candidates[0]
  );
};

const renderOnlyKey = (
  statuses: readonly RuntimePostStatus[],
  role: string
): string => {
  const existing = new Set(statuses.map((status) => statusKey(status)));
  let key = `__sked_basic_${role}__`;
  while (existing.has(key)) key += "_";
  return key;
};

/**
 * Returns the statuses the client may expose for the user's current
 * entitlement. The Basic projection intentionally mirrors the server
 * migration's deterministic target plan using existing writable IDs:
 * keys must have one explicit boolean publishing semantic, canonical labels
 * win before workflow order, and conflicting or indeterminate keys are never
 * selected as targets. Existing source keys are retained as aliases so reads
 * and API filters remain correct while migration is pending. If either role
 * has no safe ID, placeholders are rendered with writes/filtering disabled.
 */
export function getEffectivePostStatuses(
  user: User | null | undefined
): CollaborationPostStatus[] {
  const postStatuses = user?.postStatuses ?? [];
  if (!user || !hasAuthoritativeBasicPostStatuses(user)) {
    return postStatuses;
  }
  // A partial account payload is not an authoritative malformed workflow.
  // Keep persisted filter intent untouched until hydration supplies the user.
  if (!user.userId && postStatuses.length === 0) return postStatuses;

  const cached = effectiveBasicStatusesCache.get(user);
  if (cached?.source === user.postStatuses) {
    return cached.result;
  }

  const runtimeStatuses = postStatuses as RuntimePostStatus[];
  const nonPublishingKeys = safeSemanticKeys(runtimeStatuses, false);
  const publishingKeys = safeSemanticKeys(runtimeStatuses, true);
  const workInProgress = chooseTargetStatus(
    runtimeStatuses,
    nonPublishingKeys,
    WORK_IN_PROGRESS_LABEL
  );
  const workInProgressKey =
    (workInProgress && statusKey(workInProgress)) ??
    renderOnlyKey(runtimeStatuses, "work_in_progress");
  const approvedToPublish = chooseTargetStatus(
    runtimeStatuses,
    publishingKeys,
    APPROVED_TO_PUBLISH_LABEL,
    workInProgressKey
  );
  const approvedToPublishKey =
    (approvedToPublish && statusKey(approvedToPublish)) ??
    renderOnlyKey(runtimeStatuses, "approved_to_publish");
  // The migration owns missing IDs. Until it repairs an invalid catalog,
  // display both roles but permit neither status writes nor role filtering.
  const readOnly = !workInProgress || !approvedToPublish;
  const isCanonical =
    runtimeStatuses.length === 2 &&
    workInProgress?.label === WORK_IN_PROGRESS_LABEL &&
    approvedToPublish?.label === APPROVED_TO_PUBLISH_LABEL &&
    runtimeStatuses.filter((status) => status.isDefault === true).length === 1;
  // Preserve the source workflow's default publishing semantics, including
  // while migration is pending. Missing, ambiguous, or unsafe defaults fail
  // closed so an absent post status cannot become publishable on read.
  const sourceDefaults = runtimeStatuses.filter(
    (status) => status.isDefault === true
  );
  const sourceDefaultKey =
    sourceDefaults.length === 1 ? statusKey(sourceDefaults[0]) : undefined;
  const defaultKey =
    sourceDefaultKey && publishingKeys.has(sourceDefaultKey)
      ? approvedToPublishKey
      : workInProgressKey;
  const result: CollaborationPostStatus[] = [
    {
      index: 1,
      label: WORK_IN_PROGRESS_LABEL,
      isPublishingAllowed: false,
      isDefault: !readOnly && defaultKey === workInProgressKey,
      color: "#c6c6c6",
      key: workInProgressKey,
    },
    {
      index: 2,
      label: APPROVED_TO_PUBLISH_LABEL,
      isPublishingAllowed: true,
      isDefault: !readOnly && defaultKey === approvedToPublishKey,
      color: "#f6b324",
      key: approvedToPublishKey,
    },
  ];

  const sourceToTarget = new Map<string, string>();
  for (const status of orderedStatuses(runtimeStatuses)) {
    const key = statusKey(status);
    if (!key || sourceToTarget.has(key)) continue;
    sourceToTarget.set(
      key,
      publishingKeys.has(key) ? approvedToPublishKey : workInProgressKey
    );
  }
  const targetToSources = new Map<string, readonly string[]>([
    [
      workInProgressKey,
      [...sourceToTarget]
        .filter(([, targetKey]) => targetKey === workInProgressKey)
        .map(([sourceKey]) => sourceKey),
    ],
    [
      approvedToPublishKey,
      [...sourceToTarget]
        .filter(([, targetKey]) => targetKey === approvedToPublishKey)
        .map(([sourceKey]) => sourceKey),
    ],
  ]);
  basicProjectionMetadata.set(result, {
    workInProgressKey,
    defaultKey,
    readOnly,
    pendingMigration: !isCanonical,
    sourceToTarget,
    targetToSources,
  });
  effectiveBasicStatusesCache.set(user, {
    source: user.postStatuses,
    result,
  });
  return result;
}

export function hasEffectiveBasicPostStatusProjection(
  statuses: readonly { key: string }[]
): boolean {
  return basicProjectionMetadata.has(statuses);
}

export function isReadOnlyPostStatusCatalog(
  statuses: readonly { key: string }[]
): boolean {
  return basicProjectionMetadata.get(statuses)?.readOnly === true;
}

export function isPostStatusMigrationPending(
  statuses: readonly { key: string }[]
): boolean {
  return basicProjectionMetadata.get(statuses)?.pendingMigration === true;
}

export function findEffectivePostStatus<T extends { key: string }>(
  statuses: readonly T[],
  key: string | null | undefined
): T | undefined {
  if (!key) {
    const projection = basicProjectionMetadata.get(statuses);
    return projection
      ? statuses.find((status) => status.key === projection.defaultKey)
      : undefined;
  }

  const directMatch = statuses.find((status) => status.key === key);
  if (directMatch) return directMatch;

  const projection = basicProjectionMetadata.get(statuses);
  if (!projection) return undefined;
  const effectiveKey =
    projection.sourceToTarget.get(key) ?? projection.workInProgressKey;
  return statuses.find((status) => status.key === effectiveKey);
}

export function sanitizePostStatusKeys(
  keys: unknown,
  statuses: readonly { key: string }[]
): string[] {
  if (!Array.isArray(keys) || isReadOnlyPostStatusCatalog(statuses)) return [];

  const validKeys = new Set(statuses.map((status) => status.key));
  const projection = basicProjectionMetadata.get(statuses);
  return [
    ...new Set(
      keys.flatMap((key) => {
        if (typeof key !== "string" || key === "*") return [];
        if (validKeys.has(key)) return [key];
        const effectiveKey = projection?.sourceToTarget.get(key);
        return effectiveKey ? [effectiveKey] : [];
      })
    ),
  ];
}

export function expandPostStatusQueryKeys(
  keys: unknown,
  statuses: readonly { key: string }[]
): string[] {
  const effectiveKeys = sanitizePostStatusKeys(keys, statuses);
  const projection = basicProjectionMetadata.get(statuses);
  if (!projection) return effectiveKeys;

  return [
    ...new Set(
      effectiveKeys.flatMap((key) => {
        const sourceKeys = projection.targetToSources.get(key);
        return sourceKeys?.length ? sourceKeys : [key];
      })
    ),
  ];
}
